import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from "react";
import { Grid, Typography } from "@material-ui/core";
import { fieldOptionListVariableName, useCalculationField, useDispatch, useTemplateDebugManager, useVariableData } from "@ploy-lib/calculation";
import { DploySelectionField } from "@ploy-ui/form-fields";
import { FormattedNumber, defineMessages, useIntl } from "react-intl";
import { addRegisteredSectionLayout } from "../../section/sectionLayoutDescriptions";
import { ensureCollectionHasIds, getDebtId } from "./LimitedDebtField";
import { LabelComponent as Label } from "../FieldContainer";
import { ApplicationDebtSource, ErrorDisplay } from "@ploy-lib/types";
import { useFormikContext } from "formik";
const messages = defineMessages({
    whatLoansToFulfill: {
        id: "template-form.field.limitedDebtFulfillment.whatLoansToFulfill",
        defaultMessage: "Which loans should be fulfilled or paid down"
    },
    whatLoansToFulfillHelper: {
        id: "template-form.field.limitedDebtFulfillment.whatLoansToFulfillHelper",
        defaultMessage: "Choose which loans to fulfill or pay down. If the loan or loans you want to refinance are not in the list below, you may add them on the previous step"
    },
    loanFrom: {
        id: "template-form.field.limitedDebtFulfillment.loanFrom",
        defaultMessage: "{loanType} from {lender}"
    },
    validAccountNumberisRequired: {
        id: "template-form.field.limitedDebtFulfillment.validAccountNumberisRequired",
        defaultMessage: "A valid bank account number is required"
    },
    loanNumber: {
        id: "template-form.field.limitedDebtFulfillment.loanNumber",
        defaultMessage: "Account number"
    }
});
const LabelComponent = ({ debt }) => {
    const intl = useIntl();
    const loanTypes = (useVariableData("Calculator", fieldOptionListVariableName("LoanTypeOptionHelper")).value ?? []);
    const localizedLoanType = loanTypes.find(l => l.key === debt.loanType)?.value ?? debt.loanType;
    const loanFromText = intl.formatMessage(messages.loanFrom, {
        loanType: localizedLoanType,
        lender: debt.lender
    });
    const amountText = intl.formatNumber(debt.amount, {
        format: "currency",
        minimumFractionDigits: 0
    });
    return (_jsxs(Grid, { container: true, spacing: 2, direction: "column", children: [_jsxs(Grid, { container: true, item: true, spacing: 1, justifyContent: "space-between", role: "group", "aria-label": `${loanFromText} ${amountText}`, children: [_jsx(Grid, { item: true, children: _jsx(Typography, { style: { fontWeight: "bold" }, "aria-hidden": true, children: loanFromText }) }), _jsx(Grid, { item: true, style: { flex: "1", textAlign: "right", minWidth: "unset" }, children: _jsx(Typography, { style: { fontWeight: "bold" }, "aria-hidden": true, children: _jsx(FormattedNumber, { value: debt.amount, format: "currency", maximumFractionDigits: 0, minimumFractionDigits: 0 }) }) })] }), _jsxs(Grid, { container: true, item: true, spacing: 1, justifyContent: "space-between", children: [_jsx(Grid, { item: true, children: _jsx(Typography, { children: intl.formatMessage(messages.loanNumber) }) }), _jsx(Grid, { item: true, style: {
                            textAlign: "right",
                            flex: "1",
                            minWidth: "unset"
                        }, children: _jsx(Typography, { children: debt.loanNumber }) })] })] }));
};
export function LimitedDebtsFulfillmentSection(props) {
    const intl = useIntl();
    const dispatch = useDispatch();
    const debtsField = useCalculationField({
        namespace: "Calculator",
        name: "Debts"
    });
    const refindebtsField = useCalculationField({
        namespace: "Calculator",
        name: "RefinancedDebts"
    });
    const debtList = useMemo(() => ensureCollectionHasIds(debtsField?.value), [debtsField?.value]);
    const creditPurposeField = useCalculationField({
        namespace: "PRIVATE_CUSTOMER_DATA",
        name: "CreditPurpose"
    });
    const creditPurposeValue = creditPurposeField?.value ?? [];
    const relevantDebts = useMemo(() => 
    // Make sure this filter matches the filter for debts in DR: 'Debts - F85FA406-64DF-4AE1-AF6C-203A05575E43'
    debtList.filter(d => d.loanType != null &&
        // Filter out loans from debt registry because we do not know their origin
        d.source !== ApplicationDebtSource.DebtRegistry &&
        // Also filter out existing customer engagement from backoffice
        d.source !== ApplicationDebtSource.CustomerEngagement) ?? [], [debtList]);
    const { enabled: debugEnabled } = useTemplateDebugManager();
    const [showHelperText, setShowHelperText] = useState(false);
    const toggleHelperText = useCallback((e) => {
        setShowHelperText(show => !show);
        e.stopPropagation();
    }, []);
    const { touched } = useFormikContext();
    const isCalcFieldTouched = !!touched["Calculator"]?.["RefinancedDebts"];
    const isError = useMemo(() => isCalcFieldTouched && Boolean(refindebtsField.error), [isCalcFieldTouched, refindebtsField]);
    const baseId = `limited-debt-fulfillment-${props.formTemplateSectionId}`;
    const labelId = `${baseId}-label`;
    const helperTextId = `${baseId}-helper-text`;
    if (!debugEnabled && !creditPurposeValue.includes("REFINANCING")) {
        return _jsx(_Fragment, {});
    }
    if (relevantDebts.length === 0) {
        return debugEnabled ? (_jsx(Grid, { container: true, spacing: 1, className: props.className, onClick: props.onClick, children: _jsx(Grid, { item: true, xs: true, children: _jsx(Typography, { children: "No content here, just helping debugging :)" }) }) })) : null;
    }
    const relevantDebtsKeyValue = relevantDebts.map(d => {
        return {
            key: getDebtId(d),
            value: _jsx(LabelComponent, { debt: d })
        };
    });
    const selectedDebtsIds = relevantDebts
        .filter(d => d.refinance)
        .map(d => getDebtId(d));
    return (_jsxs(Grid, { container: true, spacing: 1, className: props.className, onClick: props.onClick, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(Label, { labelText: _jsx(Typography, { id: labelId, variant: "body2", style: { fontWeight: "bold" }, children: intl.formatMessage(messages.whatLoansToFulfill) }), showHelperText: showHelperText, toggleHelperText: toggleHelperText, trimmedHelperText: intl.formatMessage(messages.whatLoansToFulfillHelper), helperTextId: helperTextId }) }), _jsx(DploySelectionField, { items: relevantDebtsKeyValue, fullWidth: true, value: selectedDebtsIds, error: isError, helperText: refindebtsField.error, errorDisplay: ErrorDisplay.Touched, "aria-labelledby": labelId, multiple: true, onChange: selectedValue => {
                    const updatedList = debtList.map(rd => {
                        if (selectedValue.includes(getDebtId(rd)) ||
                            rd.source === ApplicationDebtSource.CustomerEngagement) {
                            rd.refinance = true;
                        }
                        else
                            rd.refinance = false;
                        return rd;
                    });
                    dispatch({
                        type: "patch",
                        payload: {
                            patches: [
                                {
                                    target: "Debts",
                                    namespace: "Calculator",
                                    value: updatedList,
                                    overwrite: true,
                                    changeTrigger: "Debts_manualSave"
                                }
                            ]
                        }
                    });
                } })] }));
}
addRegisteredSectionLayout({
    displayName: "LimitedDebtsFulfillmentSection",
    name: "LimitedDebtsFulfillmentSection",
    settings: {}
});
LimitedDebtsFulfillmentSection.displayName = "LimitedDebtFulfillment";
//# sourceMappingURL=LimitedDebtFulfillment.js.map